// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
// Licensed under the Apache License, Version 2.0 (https://www.apache.org/licenses/LICENSE-2.0).

// Routes (design 3.2).
import type { ReactElement } from 'react';
import { Navigate, type RouteObject } from 'react-router';
import { AUTHOR_ROLES } from '@bda/shared';
import { AuthCallback, defaultHome, RequireAuth, RequireRole, useAuth } from './auth/AuthProvider';
import { CALLBACK_PATH } from './auth/auth';
import { Layout } from './components/Layout';
import { setsEnabled } from './config';
import {
  AdminBlueprintPage,
  AdminBlueprintsPage,
  AdminDashboardPage,
  AdminUserPage,
  AdminUsersPage,
} from './features/admin/AdminPages';
import { BlueprintDetailPage } from './features/blueprints/BlueprintDetailPage';
import { EditorPage } from './features/blueprints/EditorPage';
import { HistoryPage } from './features/runs/HistoryPage';
import { LibraryPage } from './features/blueprints/LibraryPage';
import { NoAccessPage } from './auth/NoAccessPage';
import { MyPackagesPage, NewPackagePage, PackagePage, SharedPage } from './features/packages/PackagePages';
import { RunPage } from './features/runs/RunPage';
import { RunResultPage } from './features/runs/RunResultPage';
import { NewSetPage, SetDetailPage, SetListPage, SetVersionPage } from './features/sets/SetPages';
import { SetProposalPage } from './features/sets/SetProposalPage';

function Home() {
  const { user } = useAuth();
  return <Navigate to={defaultHome(user)} replace />;
}

const author = (el: ReactElement) => <RequireRole roles={[...AUTHOR_ROLES]}>{el}</RequireRole>;
const admin = (el: ReactElement) => <RequireRole roles={['Admin']}>{el}</RequireRole>;

/** Blueprint set pages exist only while sets are enabled (Req 14.2); otherwise the path goes home like any unknown one. */
export function RequireSets({ children }: { children: ReactElement }) {
  return setsEnabled() ? children : <Home />;
}
/** Author-only set routes (Req 1.1, 12.3); the API's 403 and 404 are the real control. */
const setAuthor = (el: ReactElement) => <RequireSets>{author(el)}</RequireSets>;

export const routes: RouteObject[] = [
  { path: CALLBACK_PATH, element: <AuthCallback /> },
  { path: '/no-access', element: <NoAccessPage /> },
  {
    element: (
      <RequireAuth>
        <Layout />
      </RequireAuth>
    ),
    children: [
      { index: true, element: <Home /> },
      { path: 'blueprints', element: author(<LibraryPage />) },
      { path: 'blueprints/new', element: author(<EditorPage key="new" />) },
      { path: 'blueprints/:id', element: author(<BlueprintDetailPage />) },
      { path: 'blueprints/:id/edit', element: author(<EditorPage />) },
      { path: 'sets', element: setAuthor(<SetListPage />) },
      { path: 'sets/new', element: setAuthor(<NewSetPage />) },
      { path: 'sets/proposals/:runId', element: setAuthor(<SetProposalPage />) },
      { path: 'sets/:id', element: setAuthor(<SetDetailPage />) },
      { path: 'sets/:id/versions/:v', element: setAuthor(<SetVersionPage />) },
      { path: 'run', element: author(<RunPage />) },
      { path: 'runs/:id', element: author(<RunResultPage />) },
      { path: 'history', element: author(<HistoryPage />) },
      { path: 'packages', element: author(<MyPackagesPage />) },
      { path: 'packages/new', element: author(<NewPackagePage />) },
      { path: 'packages/:id', element: <PackagePage /> },
      { path: 'shared', element: <SharedPage /> },
      { path: 'admin', element: admin(<AdminDashboardPage />) },
      { path: 'admin/users', element: admin(<AdminUsersPage />) },
      { path: 'admin/users/:sub', element: admin(<AdminUserPage />) },
      { path: 'admin/blueprints', element: admin(<AdminBlueprintsPage />) },
      { path: 'admin/blueprints/:id', element: admin(<AdminBlueprintPage />) },
      { path: '*', element: <Home /> },
    ],
  },
];
